import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, AlertTriangle, EyeOff, ArrowLeft, Search } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { buildSeoRegistry, auditRow } from "../data/seoRegistry";

const StatusPill = ({ status }) => {
  const map = {
    Good: { cls: "bg-green-100 text-green-700", Icon: Check },
    Issues: { cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
    Noindex: { cls: "bg-slate-200 text-slate-600", Icon: EyeOff },
  };
  const { cls, Icon } = map[status] || map.Noindex;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}><Icon size={12} /> {status}</span>;
};

const Dot = ({ on }) => <span className={`inline-block h-2.5 w-2.5 rounded-full ${on ? "bg-brand-terracotta" : "bg-brand-line"}`} />;

export default function SeoDashboard() {
  useSeo({ title: "SEO Dashboard | YogaToYacht", path: "/admin/seo", noindex: true });

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => buildSeoRegistry().map((r) => ({ ...r, ...auditRow(r) })), []);

  const stats = useMemo(() => ({
    total: rows.length,
    good: rows.filter((r) => r.status === "Good").length,
    issues: rows.filter((r) => r.status === "Issues").length,
    noindex: rows.filter((r) => r.status === "Noindex").length,
  }), [rows]);

  const filtered = rows.filter((r) => {
    if (filter === "issues" && r.status !== "Issues") return false;
    if (filter === "indexable" && !r.indexable) return false;
    if (filter === "noindex" && r.indexable) return false;
    if (q && !`${r.path} ${r.title}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="seo-dashboard" className="min-h-screen bg-brand-bg2 text-brand-ink">
      <header className="border-b border-brand-line bg-white">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-10">
          <div>
            <p className="overline text-brand-inkSoft">YogaToYacht · internal</p>
            <h1 className="font-display text-2xl text-brand-ink">SEO Dashboard</h1>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm text-brand-ink hover:bg-brand-bg2"><ArrowLeft size={15} /> Back to site</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-6 py-8 md:px-10">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { k: "total", label: "Pages tracked", v: stats.total },
            { k: "good", label: "Good", v: stats.good },
            { k: "issues", label: "With issues", v: stats.issues },
            { k: "noindex", label: "Noindex (hidden)", v: stats.noindex },
          ].map((s) => (
            <div key={s.k} data-testid={`seo-stat-${s.k}`} className="rounded-2xl border border-brand-line bg-white p-5">
              <p className="overline text-brand-inkSoft">{s.label}</p>
              <p className="font-display mt-2 text-3xl text-brand-ink">{s.v}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
            <input data-testid="seo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search path or title…" className="rounded-full border border-brand-line bg-white py-2 pl-9 pr-4 text-sm outline-none focus:border-brand-ink" />
          </div>
          {["all", "issues", "indexable", "noindex"].map((f) => (
            <button key={f} data-testid={`seo-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === f ? "bg-brand-ink text-white" : "border border-brand-line bg-white text-brand-ink hover:bg-brand-bg2"}`}>{f}</button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-brand-line bg-white">
          <table data-testid="seo-table" className="w-full min-w-[1100px] text-left text-sm">
            <thead className="border-b border-brand-line text-brand-inkSoft">
              <tr>
                <th className="p-3 font-medium">Page</th>
                <th className="p-3 font-medium">Title / Description / H1 / Canonical</th>
                <th className="p-3 font-medium">Sitemap</th>
                <th className="p-3 font-medium">SEO status</th>
                <th className="p-3 font-medium">Issues &amp; suggested fixes</th>
                <th className="p-3 font-medium">Monetization</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.path} data-testid={`seo-row-${r.path}`} className="border-b border-brand-line/60 align-top">
                  <td className="p-3">
                    <Link to={r.path} className="font-medium text-brand-ink link-underline">{r.path}</Link>
                    <div className="mt-1 text-xs text-brand-inkSoft">{r.group}</div>
                  </td>
                  <td className="max-w-[420px] p-3">
                    <p className="font-medium text-brand-ink">{r.title}</p>
                    <p className="mt-1 text-xs text-brand-inkSoft">{r.description}</p>
                    <p className="mt-1 text-xs"><span className="text-brand-inkSoft">H1:</span> {r.h1}</p>
                    <p className="mt-0.5 break-all text-xs text-brand-inkSoft">{r.canonical}</p>
                  </td>
                  <td className="p-3">{r.inSitemap ? <span className="text-green-700">In sitemap</span> : <span className="text-brand-inkSoft">Excluded</span>}</td>
                  <td className="p-3"><StatusPill status={r.status} /></td>
                  <td className="max-w-[280px] p-3">
                    {r.issues.length === 0 ? <span className="text-brand-inkSoft">{r.indexable ? "No issues" : "Intentionally hidden"}</span> : (
                      <ul className="space-y-1">
                        {r.issues.map((it, i) => (
                          <li key={i} className="text-xs"><span className="text-amber-700">{it}</span> — <span className="text-brand-inkSoft">{r.fixes[i]}</span></li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3 text-xs text-brand-inkSoft">
                      <span className="inline-flex items-center gap-1"><Dot on={r.monetization.newsletter} /> News</span>
                      <span className="inline-flex items-center gap-1"><Dot on={r.monetization.affiliate} /> Aff</span>
                      <span className="inline-flex items-center gap-1"><Dot on={r.monetization.recommendation} /> Rec</span>
                      <span className="inline-flex items-center gap-1"><Dot on={r.monetization.provider} /> Prov</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-brand-inkSoft">Internal tool · not linked in navigation · marked noindex. Monetization: News = newsletter, Aff = affiliate CTA / booking, Rec = recommendation form, Prov = provider interest form.</p>
      </main>
    </div>
  );
}
